import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faClose,
  faPen,
} from '@fortawesome/sharp-solid-svg-icons';
import { useRouter } from 'next/navigation';
import React, { useCallback, useEffect, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import Button, { ButtonSize, ButtonVariant } from '@/components/button';
import IconButton from '@/components/button/iconButton';
import Typography from '@/components/typography';
import {
  INSIGHTS_CATALOG_LIBRARY_URL,
  INSIGHTS_CATALOG_URL,
} from '@/constants/urls';
import {
  InsightCatalogColor,
  InsightCatalogGradientColor,
} from '@/features/insightsCatalog/appearance/colors';
import {
  InsightsCatalogCircleLayer,
  InsightsCatalogLineLayer,
  InsightsCatalogPolygonLayer,
} from '@/features/insightsCatalog/models';
import { useInsightsCatalogContext } from '@/features/insightsCatalog/provider';
import { ReactSetState } from '@/models/reactTypes';

import { LayersOverlayRow } from './row';

type Props = {
  open: boolean;
  setOpen: ReactSetState<Boolean>;
};

/** @deprecated */
export const LayersOverlay: React.FC<Props> = ({ open, setOpen }) => {
  const {
    layersOverlayLayers,
    layers,
    getLayers,
    layersLoading,
    legendLayer,
    setLegendLayerId,
  } = useInsightsCatalogContext();

  const router = useRouter();

  useEffect(() => {
    if (!layers && !layersLoading) {
      getLayers();
    }
  }, [layersLoading, getLayers, layers]);

  const openApperance = useCallback(() => {
    if (!legendLayer) return;

    router.push(`${INSIGHTS_CATALOG_URL}/${legendLayer.id}/appearance/`);
  }, [legendLayer, router]);

  const mainColor: InsightCatalogColor | null = useMemo(() => {
    if (legendLayer?.isPolygonLayer) {
      return (legendLayer as InsightsCatalogPolygonLayer).fillColor ?? null;
    }
    if (legendLayer?.isLineLayer) {
      return (legendLayer as InsightsCatalogLineLayer).lineColor ?? null;
    }
    if (legendLayer?.isCircleLayer) {
      return (legendLayer as InsightsCatalogCircleLayer).circleColor ?? null;
    }
    return null;
  }, [legendLayer]);

  const distributionStrategy: string | null = useMemo(() => {
    if (!mainColor || mainColor?.type === 'simple') return null;

    const gradientColor = mainColor as InsightCatalogGradientColor;

    if (gradientColor.binary) {
      return null;
    }

    if (gradientColor.distributionStrategy === 'equalCount') {
      return 'Equal Count';
    }

    if (gradientColor.distincts) {
      return null;
    }

    // Equal interval is default
    return 'Equal Interval';
  }, [mainColor]);

  /**
   * We are determining the type the same way as we determine the distribution strategy because we don't store
   *   the field type but can infer it based on how the data is distributed
   */
  const fieldOfInterestType: string | null = useMemo(() => {
    if (!mainColor || mainColor?.type === 'simple') return null;

    const gradientColor = mainColor as InsightCatalogGradientColor;

    if (gradientColor.binary) {
      return '(boolean)';
    }

    if (gradientColor.distributionStrategy === 'equalCount') {
      return '(number)';
    }

    if (gradientColor.distincts) {
      return '(string)';
    }

    // Equal interval is default
    return '(number)';
  }, [mainColor]);

  const colorMap: any[] | null = useMemo(() => {
    if (!mainColor || !legendLayer) return null;

    if (mainColor.type === 'simple') {
      return [{ color: mainColor.color, value: 'All' }];
    }

    if (mainColor.type === 'gradient') {
      return legendLayer.buildColorGradientLegend(mainColor);
    }

    return null;
  }, [legendLayer, mainColor]);

  if (legendLayer) {
    return (
      <div
        className={twMerge(
          'dark z-[100] flex h-[476px] w-[310px] grow flex-col overflow-auto !bg-gray-90',
          !open && 'hidden'
        )}
      >
        <div className="flex flex-col pb-2">
          <div className="flex justify-between p-4 pb-1">
            <Button
              className="flex items-center gap-x-2"
              size={ButtonSize.SMALL}
              variant={ButtonVariant.GHOST}
              onClick={() => {
                setOpen(true);
                setLegendLayerId(null);
              }}
            >
              <FontAwesomeIcon className="text-white" icon={faArrowLeft} />
              <Typography className="text-white" variant="button1">
                Favorite Layers
              </Typography>
            </Button>

            <IconButton
              className="bg-gray-90 p-2 hover:bg-gray-70"
              size={ButtonSize.SMALL}
              variant={ButtonVariant.GHOST}
              onClick={() => {
                setLegendLayerId(null);
                setOpen(false);
              }}
            >
              <FontAwesomeIcon className="h-4 w-4 text-white" icon={faClose} />
            </IconButton>
          </div>

          <div className="mb-4 flex flex-col gap-y-2 overflow-auto px-4">
            <div className="flex items-start justify-between">
              <div className="flex flex-col pt-1">
                <Typography
                  className="line-clamp-2"
                  title={`${legendLayer.name} Legend`}
                  variant="button2"
                >
                  {legendLayer.name} Legend
                </Typography>

                <div className="flex flex-col gap-y-1">
                  <Typography
                    className="truncate whitespace-nowrap"
                    variant="body2"
                  >
                    {
                      (mainColor as InsightCatalogGradientColor)
                        ?.fieldOfInterest
                    }{' '}
                    {fieldOfInterestType ?? ''}
                  </Typography>
                  {distributionStrategy && (
                    <Typography variant="caption">
                      {distributionStrategy}
                    </Typography>
                  )}
                  {((mainColor as InsightCatalogGradientColor).minValue ||
                    (mainColor as InsightCatalogGradientColor).maxValue) &&
                    distributionStrategy === 'Equal Interval' && (
                      <Typography variant="caption">
                        Range of Interest:{' '}
                        {(mainColor as InsightCatalogGradientColor).minValue} -{' '}
                        {(mainColor as InsightCatalogGradientColor).maxValue}
                      </Typography>
                    )}
                </div>
              </div>

              <IconButton
                className="bg-gray-90 hover:bg-gray-70"
                size={ButtonSize.SMALL}
                variant={ButtonVariant.GHOST}
                onClick={openApperance}
              >
                <FontAwesomeIcon className="text-white" icon={faPen} />
              </IconButton>
            </div>

            <div className="flex flex-col gap-y-2">
              {colorMap?.map((c) => (
                <div
                  key={`${c.color}-${c.value}`}
                  className="flex items-center gap-x-4 truncate"
                >
                  <div
                    className="h-4 w-4 rounded-sm border border-2 border-white"
                    style={{ backgroundColor: c.color }}
                  />
                  <Typography
                    className="truncate"
                    title={c.value}
                    variant="body2"
                  >
                    {c.value}
                  </Typography>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={twMerge(
        'dark z-[100] flex h-[476px] w-[310px] flex-col !bg-gray-90',
        !open && 'hidden'
      )}
    >
      <div className="flex items-center justify-between p-4">
        <Typography variant="button1">Favorite Layers</Typography>
        <IconButton
          size={ButtonSize.SMALL}
          variant={ButtonVariant.GHOST}
          onClick={() => setOpen(false)}
        >
          <FontAwesomeIcon className="text-white" icon={faClose} />
        </IconButton>
      </div>

      {/* List body */}
      <div className="flex flex-1 flex-col overflow-auto">
        {!layersOverlayLayers?.length && (
          <div className="flex flex-col items-center justify-center gap-y-4 px-4">
            <Typography variant="body2">
              Favorite layers will show up here for quick access.
            </Typography>
            <Button
              className="w-[150px] border-white text-white"
              size={ButtonSize.SMALL}
              variant={ButtonVariant.SECONDARY_BLACK}
              onClick={() => router.push(INSIGHTS_CATALOG_LIBRARY_URL)}
            >
              View All Layers
            </Button>
          </div>
        )}

        {layersOverlayLayers?.map((layer) => {
          return <LayersOverlayRow key={layer.id} layer={layer} />;
        })}
      </div>
    </div>
  );
};
